/* TWRD: photo rotation for the "image with text overlap" cards.

   Photo 1 is the card's normal image. Photos 2+ are layers on top at
   opacity 0 ([data-twrd-ovr-layer]). Showing photo n fades its layer in;
   showing photo 1 fades the active layer out.

   Timer: one requestAnimationFrame loop per card. It runs only while the
   card is on screen and the tab is visible, so it pauses and resumes in
   place. Photos change with a blur dissolve, same as the collection hero.
   The first cycle is longer per card (1.5 s steps), so the cards
   do not change at the same moment. The progress fill (--twrd-p, see
   twrd-slide-progress.css) follows the same timer.

   prefers-reduced-motion: no timer, photo 1 stays. The dots still work. */
(function () {
  'use strict';

  var STAGGER_MS = 1500;
  var FADE_MS = 1300;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function reducedMotion() {
    return Boolean(reduce && reduce.matches);
  }

  function initCard(card, order) {
    if (card.dataset.twrdOvrReady === '1') return;
    var layers = Array.prototype.slice.call(card.querySelectorAll('[data-twrd-ovr-layer]'));
    var dotWrap = card.querySelector('[data-twrd-ovr-dots]');
    var dots = dotWrap ? Array.prototype.slice.call(dotWrap.querySelectorAll('[data-twrd-ovr-dot]')) : [];
    if (!layers.length) return;
    card.dataset.twrdOvrReady = '1';

    var total = layers.length + 1;
    var interval = parseInt(card.getAttribute('data-interval'), 10) || 3000;
    var current = 0;
    var elapsed = 0;
    var cycle = interval + order * STAGGER_MS;
    var onScreen = false;
    var rafId = 0;
    var lastTs = 0;
    var underTimer = 0;

    function layerFor(index) {
      return index > 0 ? layers[index - 1] : null;
    }

    function setProgress(value) {
      if (!dotWrap) return;
      var v = value > 0 ? (value > 1 ? 1 : value) : 0;
      dotWrap.style.setProperty('--twrd-p', v.toFixed(4));
    }

    function paintDots() {
      dots.forEach(function (dot, i) {
        var on = i === current;
        dot.classList.toggle('is-active', on);
        dot.classList.toggle('is-past', i < current);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    var layerWrap = card.querySelector('[data-twrd-ovr-layers]');
    var hazeTimer = 0;

    /* Blur dissolve (2026-10-22): warm haze mid-way, and photo 1 settles
       from soft to sharp when it comes back. CSS does the rest. */
    function dissolve(toBase) {
      if (reducedMotion()) return;
      if (layerWrap) {
        layerWrap.classList.remove('is-hazing');
        void layerWrap.offsetWidth;
        layerWrap.classList.add('is-hazing');
        window.clearTimeout(hazeTimer);
        hazeTimer = window.setTimeout(function () { layerWrap.classList.remove('is-hazing'); }, FADE_MS);
      }
      if (current === 0 && !toBase) {
        /* Photo 1 leaves: it blurs up under the incoming layer, then resets. */
        card.classList.add('is-base-out');
        window.setTimeout(function () {
          card.classList.add('is-base-soft');
          card.classList.remove('is-base-out');
          window.requestAnimationFrame(function () { card.classList.remove('is-base-soft'); });
        }, FADE_MS + 50);
      }
      if (toBase) {
        card.classList.remove('is-base-out');
        card.classList.add('is-base-soft');
        void card.offsetWidth;
        window.requestAnimationFrame(function () { card.classList.remove('is-base-soft'); });
      }
    }

    function show(index) {
      if (index === current || index < 0 || index >= total) return;
      dissolve(index === 0);
      var prev = layerFor(current);
      var next = layerFor(index);

      window.clearTimeout(underTimer);
      layers.forEach(function (layer) {
        if (layer !== prev && layer !== next) layer.classList.remove('is-active', 'is-under');
      });

      if (next) {
        /* Keep the old photo fully visible under the new one while it fades in. */
        if (prev) {
          prev.classList.add('is-under');
          prev.classList.remove('is-active');
        }
        next.classList.remove('is-under');
        next.classList.add('is-active');
        if (prev) {
          underTimer = window.setTimeout(function () {
            prev.classList.remove('is-under');
          }, FADE_MS);
        }
      } else if (prev) {
        /* Back to photo 1: fade the top layer out. */
        prev.classList.remove('is-active', 'is-under');
      }

      current = index;
      elapsed = 0;
      cycle = interval;
      setProgress(0);
      paintDots();
    }

    function running() {
      return onScreen && !document.hidden && !reducedMotion();
    }

    function tick(ts) {
      rafId = 0;
      if (!running()) return;
      if (lastTs) elapsed += Math.min(ts - lastTs, 100);
      lastTs = ts;
      if (elapsed >= cycle) {
        show((current + 1) % total);
      } else {
        setProgress(elapsed / cycle);
      }
      rafId = window.requestAnimationFrame(tick);
    }

    function sync() {
      if (dotWrap) dotWrap.classList.toggle('is-timed', !reducedMotion());
      if (running()) {
        if (!rafId) {
          lastTs = 0;
          rafId = window.requestAnimationFrame(tick);
        }
      } else if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = 0;
      }
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        show(parseInt(dot.getAttribute('data-index'), 10) || 0);
        sync();
      });
    });

    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          onScreen = entry.isIntersecting;
        });
        sync();
      }, { threshold: 0.15 });
      io.observe(card);
    } else {
      onScreen = true;
    }

    function onMotionChange() {
      if (reducedMotion()) show(0);
      sync();
    }

    document.addEventListener('visibilitychange', sync);
    if (reduce && reduce.addEventListener) reduce.addEventListener('change', onMotionChange);

    /* Theme editor: drop the document-level listeners, observer and timers
       when the section is unloaded, so reloads do not pile up. */
    card.__twrdOvrTeardown = function () {
      document.removeEventListener('visibilitychange', sync);
      if (reduce && reduce.removeEventListener) reduce.removeEventListener('change', onMotionChange);
      if (io) io.disconnect();
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = 0;
      window.clearTimeout(underTimer);
      delete card.__twrdOvrTeardown;
      delete card.dataset.twrdOvrReady;
    };

    paintDots();
    setProgress(0);
    sync();
  }

  function boot(root) {
    var sections = (root || document).querySelectorAll('[data-section-type="image-with-text-overlap"]');
    Array.prototype.forEach.call(sections, function (section) {
      var cards = section.querySelectorAll('[data-twrd-ovr]');
      Array.prototype.forEach.call(cards, function (card, i) {
        initCard(card, i);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var cards = event.target.querySelectorAll('[data-twrd-ovr]');
    Array.prototype.forEach.call(cards, function (card) {
      if (card.__twrdOvrTeardown) card.__twrdOvrTeardown();
    });
  });
})();
